import { useState } from "react";
import { C, uid, fmt, Btn, Input, Select, Textarea, Section, StatCard, Tabs, useIsMobile } from "./ui";

const CATS_INGRESO = ["Venta de animales","Lana / Productos","Arrendamiento","Subsidio / Programa","Otro ingreso"];
const CATS_EGRESO  = ["Compra de animales","Insumos agropecuarios","Combustible","Mano de obra","Veterinaria","Maquinaria","Semillas","Servicios","Otro gasto"];

function fmtMoney(n) {
  if (n === null || n === undefined || (n !== 0 && !n)) return "—";
  return new Intl.NumberFormat("es-UY", { maximumFractionDigits: 0 }).format(Math.abs(n));
}

// ─── TAB RESUMEN (P&L) ────────────────────────────────────
function TabResumen({ transacciones }) {
  const hoy = new Date();
  const mesActual = hoy.toISOString().slice(0, 7);
  const [periodo, setPeriodo] = useState("mes");          // mes | todo | rango
  const [desde, setDesde] = useState(mesActual + "-01");
  const [hasta, setHasta] = useState(hoy.toISOString().slice(0, 10));

  const enRango = (t) => {
    const f = t.fecha || "";
    if (periodo === "todo") return true;
    if (periodo === "mes")  return f.slice(0, 7) === mesActual;
    return f >= desde && f <= hasta;
  };
  const trans = transacciones.filter(enRango);

  const ingresos = trans.filter(t => t.tipo === "ingreso");
  const egresos  = trans.filter(t => t.tipo === "egreso");
  const totalI   = ingresos.reduce((s, t) => s + (t.monto || 0), 0);
  const totalE   = egresos.reduce((s,  t) => s + (t.monto || 0), 0);
  const resultado = totalI - totalE;
  const margen    = totalI > 0 ? Math.round((resultado / totalI) * 100) : 0;
  const maxBar    = Math.max(totalI, totalE, 1);

  const byCategoria = list => {
    const map = {};
    list.forEach(t => { map[t.categoria] = (map[t.categoria] || 0) + (t.monto || 0); });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  };
  const cats_i = byCategoria(ingresos);
  const cats_e = byCategoria(egresos);
  const maxI = cats_i[0]?.[1] || 1;
  const maxE = cats_e[0]?.[1] || 1;

  const periodos = [
    { v: "mes",   l: "Mes actual" },
    { v: "todo",  l: "Histórico" },
    { v: "rango", l: "Rango" },
  ];

  return (
    <div>
      {/* Selector de período */}
      <div style={{ display:"flex", gap:6, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
        <div style={{ display:"flex", gap:4, background:C.arena, borderRadius:8, padding:3 }}>
          {periodos.map(p => (
            <button key={p.v} onClick={()=>setPeriodo(p.v)} style={{
              padding:"6px 14px", fontSize:12, border:"none", cursor:"pointer", borderRadius:6,
              background: periodo===p.v ? C.tierra : "transparent",
              color: periodo===p.v ? "#fff" : C.muted, fontWeight: periodo===p.v ? 700 : 400 }}>
              {p.l}
            </button>
          ))}
        </div>
        {periodo==="rango" && (
          <div style={{ display:"flex", gap:6, alignItems:"center", fontSize:12, color:C.muted }}>
            <input type="date" value={desde} onChange={e=>setDesde(e.target.value)}
              style={{ padding:"6px 8px", border:`1.5px solid ${C.arenaDark}`, borderRadius:7, fontSize:13 }}/>
            <span>a</span>
            <input type="date" value={hasta} onChange={e=>setHasta(e.target.value)}
              style={{ padding:"6px 8px", border:`1.5px solid ${C.arenaDark}`, borderRadius:7, fontSize:13 }}/>
          </div>
        )}
      </div>

      {trans.length === 0 ? (
        <div style={{ textAlign:"center", padding:"50px 20px", color:C.muted }}>
          <div style={{ fontSize:40, marginBottom:14 }}>📊</div>
          <div style={{ fontSize:14 }}>Sin movimientos en este período.</div>
        </div>
      ) : (
      <>
      {/* HERO resultado + gráfico de barras comparativo */}
      <div style={{ display:"grid", gridTemplateColumns:"1.3fr 1fr", gap:16, marginBottom:22,
        background:`linear-gradient(135deg, ${resultado>=0?"#1F3D24":"#3D1F1F"} 0%, ${C.tierra} 100%)`,
        borderRadius:16, padding:"22px 24px", color:"#fff" }}>
        <div style={{ display:"flex", flexDirection:"column", justifyContent:"center" }}>
          <div style={{ fontSize:11, fontFamily:"monospace", textTransform:"uppercase", letterSpacing:"0.1em", opacity:0.7 }}>
            {resultado>=0 ? "Ganancia del período" : "Pérdida del período"}
          </div>
          <div style={{ fontFamily:"serif", fontSize:38, fontWeight:700, lineHeight:1.1, marginTop:4,
            color: resultado>=0 ? "#8FE0A0" : "#F0A0A0" }}>
            {resultado>=0?"+":"−"} ${fmtMoney(Math.abs(resultado))}
          </div>
          <div style={{ fontSize:12, opacity:0.8, marginTop:6 }}>
            Margen <b>{margen}%</b> sobre ingresos
          </div>
        </div>
        {/* Barras verticales ingresos vs egresos */}
        <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"center", gap:22, height:120 }}>
          {[{ l:"Ingresos", v:totalI, c:"#5FBF77" }, { l:"Egresos", v:totalE, c:"#D97070" }].map(b=>(
            <div key={b.l} style={{ display:"flex", flexDirection:"column", alignItems:"center", height:"100%", justifyContent:"flex-end" }}>
              <div style={{ fontSize:11, fontWeight:700, marginBottom:4, whiteSpace:"nowrap" }}>${fmtMoney(b.v)}</div>
              <div style={{ width:42, height:`${Math.max(4,(b.v/maxBar)*92)}px`, background:b.c,
                borderRadius:"6px 6px 0 0", transition:"height 0.4s" }}/>
              <div style={{ fontSize:10, opacity:0.8, marginTop:5 }}>{b.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Desglose por categoría */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
        {/* Ingresos */}
        <div>
          <div style={{ fontSize:11, fontFamily:"monospace", textTransform:"uppercase", letterSpacing:"0.08em", color:C.ok, marginBottom:10 }}>
            ▲ Ingresos por categoría
          </div>
          {cats_i.length === 0
            ? <div style={{ fontSize:12, color:C.muted }}>Sin ingresos</div>
            : cats_i.map(([cat, monto]) => (
            <div key={cat} style={{ marginBottom:10 }}>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:3 }}>
                <span style={{ color:C.texto }}>{cat}</span>
                <span style={{ fontFamily:"monospace", fontWeight:600, color:C.ok }}>$ {fmtMoney(monto)}</span>
              </div>
              <div style={{ height:8, background:C.arenaDark, borderRadius:4, overflow:"hidden" }}>
                <div style={{ height:8, background:C.ok, borderRadius:4, width:`${Math.round((monto/maxI)*100)}%`, transition:"width 0.3s" }}/>
              </div>
            </div>
          ))}
        </div>

        {/* Egresos */}
        <div>
          <div style={{ fontSize:11, fontFamily:"monospace", textTransform:"uppercase", letterSpacing:"0.08em", color:C.danger, marginBottom:10 }}>
            ▼ Egresos por categoría
          </div>
          {cats_e.length === 0
            ? <div style={{ fontSize:12, color:C.muted }}>Sin egresos</div>
            : cats_e.map(([cat, monto]) => (
            <div key={cat} style={{ marginBottom:10 }}>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:3 }}>
                <span style={{ color:C.texto }}>{cat}</span>
                <span style={{ fontFamily:"monospace", fontWeight:600, color:C.danger }}>$ {fmtMoney(monto)}</span>
              </div>
              <div style={{ height:8, background:C.arenaDark, borderRadius:4, overflow:"hidden" }}>
                <div style={{ height:8, background:"#D06060", borderRadius:4, width:`${Math.round((monto/maxE)*100)}%`, transition:"width 0.3s" }}/>
              </div>
            </div>
          ))}
        </div>
      </div>
      </>
      )}
    </div>
  );
}

// ─── TAB MOVIMIENTOS ──────────────────────────────────────
function TabMovimientos({ transacciones, onAdd }) {
  const [filtro,   setFiltro]   = useState("todos");
  const [showForm, setShowForm] = useState(false);
  const [tipo,     setTipo]     = useState("ingreso");
  const [categoria,setCategoria]= useState(CATS_INGRESO[0]);
  const [concepto, setConcepto] = useState("");
  const [monto,    setMonto]    = useState("");
  const [fecha,    setFecha]    = useState(new Date().toISOString().slice(0, 10));
  const [obs,      setObs]      = useState("");

  const cambiarTipo = v => {
    setTipo(v);
    setCategoria(v === "ingreso" ? CATS_INGRESO[0] : CATS_EGRESO[0]);
  };

  const cats = tipo === "ingreso" ? CATS_INGRESO : CATS_EGRESO;

  const guardar = () => {
    if (!concepto || !monto) return;
    onAdd({ id:uid(), tipo, categoria, concepto, monto:parseFloat(monto), fecha, obs });
    // Reset completo para evitar que el próximo registro herede el tipo anterior
    setShowForm(false);
    setTipo("ingreso");
    setCategoria(CATS_INGRESO[0]);
    setConcepto(""); setMonto(""); setObs("");
    setFecha(new Date().toISOString().slice(0, 10));
  };

  const filtradas = [...transacciones]
    .filter(t => filtro === "todos" || t.tipo === filtro)
    .sort((a, b) => {
      const f = (b.fecha || "").localeCompare(a.fecha || "");
      return f !== 0 ? f : (b.ts || 0) - (a.ts || 0);
    });

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        {/* Filtros */}
        <div style={{ display:"flex", gap:4, background:C.arena, borderRadius:8, padding:3 }}>
          {[{v:"todos",l:"Todos"},{v:"ingreso",l:"▲ Ingresos"},{v:"egreso",l:"▼ Egresos"}].map(f=>(
            <button key={f.v} onClick={()=>setFiltro(f.v)} style={{
              padding:"5px 12px", fontSize:12, fontFamily:"sans-serif", border:"none", cursor:"pointer",
              borderRadius:6, transition:"all 0.15s",
              background: filtro===f.v ? (f.v==="ingreso"?"#D4F0D8":f.v==="egreso"?"#FAE8E8":C.white) : "transparent",
              color: filtro===f.v ? (f.v==="ingreso"?C.ok:f.v==="egreso"?C.danger:C.tierra) : C.muted,
              fontWeight: filtro===f.v ? 700 : 400,
              boxShadow: filtro===f.v ? "0 1px 4px rgba(0,0,0,0.1)" : "none"
            }}>{f.l}</button>
          ))}
        </div>
        <Btn size="sm" variant="primary" onClick={()=>setShowForm(v=>!v)}>
          {showForm ? "Cancelar" : "+ Registrar"}
        </Btn>
      </div>

      {showForm && (
        <div style={{ borderRadius:12, padding:16, marginBottom:16, border:`2px solid ${tipo==="ingreso"?"#90CC98":"#E09090"}`,
          background: tipo==="ingreso" ? "#F0FAF2" : "#FDF0F0", transition:"all 0.2s" }}>

          {/* Selección de tipo — dos tarjetas grandes, imposibles de confundir */}
          <div style={{ fontSize:11, fontFamily:"monospace", textTransform:"uppercase", letterSpacing:"0.08em",
            color:C.muted, marginBottom:8 }}>¿Qué estás registrando?</div>
          <div style={{ display:"flex", gap:10, marginBottom:18 }}>
            {[
              { v:"ingreso", ic:"💰", t:"INGRESO", sub:"Entra plata", solid:"#2E7D46", soft:"#90CC98", txt:"#1A7A3C" },
              { v:"egreso",  ic:"💸", t:"EGRESO",  sub:"Sale plata",  solid:"#C0392B", soft:"#E0A0A0", txt:C.danger },
            ].map(o=>{
              const activo = tipo===o.v;
              return (
                <button key={o.v} onClick={()=>cambiarTipo(o.v)} style={{
                  flex:1, padding:"14px 8px", borderRadius:10, cursor:"pointer", position:"relative",
                  border: activo ? `2px solid ${o.solid}` : `2px solid ${o.soft}`,
                  background: activo ? o.solid : C.white,
                  color: activo ? "#fff" : o.txt,
                  boxShadow: activo ? "0 3px 10px rgba(0,0,0,0.18)" : "none",
                  transform: activo ? "translateY(-1px)" : "none", transition:"all 0.15s",
                  display:"flex", flexDirection:"column", alignItems:"center", gap:2,
                }}>
                  {activo && <span style={{ position:"absolute", top:6, right:8, fontSize:13 }}>✓</span>}
                  <span style={{ fontSize:26, lineHeight:1 }}>{o.ic}</span>
                  <span style={{ fontWeight:800, fontSize:15, letterSpacing:"0.03em" }}>{o.t}</span>
                  <span style={{ fontSize:11, opacity: activo?0.9:0.7 }}>{o.sub}</span>
                </button>
              );
            })}
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
            <Select label="Categoría" value={categoria} onChange={setCategoria} options={cats}/>
            <Input label="Fecha" value={fecha} onChange={setFecha} type="date"/>
            <Input label="Concepto" value={concepto} onChange={setConcepto} required placeholder="Descripción breve"/>
            <Input label="Monto ($)" value={monto} onChange={setMonto} type="number" placeholder="Ej: 5000" required/>
          </div>
          <Textarea label="Observaciones" value={obs} onChange={setObs} rows={2}/>
          <button onClick={guardar} disabled={!concepto||!monto} style={{
            width:"100%", padding:"13px", borderRadius:9, border:"none", marginTop:4,
            cursor: (!concepto||!monto) ? "not-allowed" : "pointer",
            background: (!concepto||!monto) ? "#CBC4BA" : (tipo==="ingreso" ? "#2E7D46" : "#C0392B"),
            color:"#fff", fontWeight:700, fontSize:15, transition:"all 0.15s",
          }}>
            {tipo === "ingreso" ? "💰 Guardar ingreso" : "💸 Guardar egreso"}
          </button>
        </div>
      )}

      {filtradas.length === 0 ? (
        <div style={{ textAlign:"center", padding:"30px 20px", color:C.muted, fontSize:13 }}>
          Sin movimientos con este filtro.
        </div>
      ) : filtradas.map(t => {
        const esIngreso = t.tipo === "ingreso";
        return (
          <div key={t.id} style={{ background:C.white, borderRadius:9, padding:"10px 14px", marginBottom:6,
            border:`1.5px solid ${esIngreso?"#B0DDB8":"#E0A0A0"}`,
            display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:36, height:36, borderRadius:8, flexShrink:0,
              background: esIngreso ? "#D4F0D8" : "#FAE8E8",
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:16, fontWeight:700, color: esIngreso ? C.ok : C.danger }}>
              {esIngreso ? "▲" : "▼"}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:600, fontSize:13, color:C.tierra }}>{t.concepto}</div>
              <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>
                <span style={{ padding:"1px 6px", borderRadius:10,
                  background: esIngreso?"#D4F0D8":"#FAE8E8",
                  color: esIngreso?C.ok:C.danger, fontSize:10, fontFamily:"monospace" }}>
                  {esIngreso?"INGRESO":"EGRESO"}
                </span>
                <span style={{ marginLeft:8 }}>{t.categoria}</span>
                <span style={{ fontFamily:"monospace", marginLeft:10 }}>{fmt(t.fecha)}{t.hora?` · ${t.hora}`:""}</span>
              </div>
              {t.obs && <div style={{ fontSize:11, color:C.muted, fontStyle:"italic", marginTop:2 }}>{t.obs}</div>}
            </div>
            <div style={{ textAlign:"right", flexShrink:0 }}>
              <div style={{ fontFamily:"serif", fontSize:16, fontWeight:700, color: esIngreso?C.ok:C.danger }}>
                {esIngreso ? "+" : "−"} $ {fmtMoney(t.monto)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── ECONOMIA PANEL (exported) ────────────────────────────
export function EconomiaPanel({ transacciones, onAddTransaccion }) {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState("movimientos");
  const tabs = [{ id:"movimientos", label:"Movimientos" }, { id:"resumen", label:"Resumen P&L" }];

  const totalI    = transacciones.filter(t => t.tipo === "ingreso").reduce((s,t) => s+(t.monto||0), 0);
  const totalE    = transacciones.filter(t => t.tipo === "egreso").reduce((s,t)  => s+(t.monto||0), 0);
  const resultado = totalI - totalE;

  return (
    <div style={{ display:"flex", flexDirection: isMobile?"column":"row", flex:1, overflow:"hidden" }}>
      <aside style={{ width: isMobile?"100%":280, background:C.white,
        borderRight: isMobile?"none":`1px solid ${C.arenaDark}`,
        borderBottom: isMobile?`1px solid ${C.arenaDark}`:"none",
        overflowY:"auto", flexShrink:0, maxHeight: isMobile?"45%":"none" }}>
        <Section label="Resumen económico">
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <div style={{ background:"#D4F0D8", borderRadius:9, padding:"10px 13px", border:"1px solid #90CC98" }}>
              <div style={{ fontSize:10, fontFamily:"monospace", textTransform:"uppercase", color:C.ok, marginBottom:2 }}>▲ Ingresos</div>
              <div style={{ fontFamily:"serif", fontSize:20, fontWeight:700, color:C.ok }}>$ {fmtMoney(totalI)}</div>
            </div>
            <div style={{ background:"#FAE8E8", borderRadius:9, padding:"10px 13px", border:"1px solid #E09090" }}>
              <div style={{ fontSize:10, fontFamily:"monospace", textTransform:"uppercase", color:C.danger, marginBottom:2 }}>▼ Egresos</div>
              <div style={{ fontFamily:"serif", fontSize:20, fontWeight:700, color:C.danger }}>$ {fmtMoney(totalE)}</div>
            </div>
            <div style={{ background:resultado>=0?"#D4F0D8":"#FAE8E8", borderRadius:9, padding:"10px 13px",
              border:`1px solid ${resultado>=0?"#90CC98":"#E09090"}` }}>
              <div style={{ fontSize:10, fontFamily:"monospace", textTransform:"uppercase", color:resultado>=0?C.ok:C.danger, marginBottom:2 }}>
                Resultado neto
              </div>
              <div style={{ fontFamily:"serif", fontSize:20, fontWeight:700, color:resultado>=0?C.ok:C.danger }}>
                {resultado>=0?"+":"−"} $ {fmtMoney(Math.abs(resultado))}
              </div>
            </div>
          </div>
        </Section>

        <Section label="Últimos movimientos">
          {transacciones.length === 0
            ? <div style={{ fontSize:12, color:C.muted }}>Sin movimientos aún</div>
            : [...transacciones].sort((a,b)=>b.fecha.localeCompare(a.fecha)).slice(0,5).map(t=>{
              const esI = t.tipo==="ingreso";
              return (
                <div key={t.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                  <div>
                    <div style={{ fontSize:12, color:C.texto }}>{t.concepto}</div>
                    <div style={{ fontSize:10, color:C.muted, fontFamily:"monospace" }}>{fmt(t.fecha)}</div>
                  </div>
                  <span style={{ fontSize:12, fontWeight:700, color:esI?C.ok:C.danger, fontFamily:"monospace" }}>
                    {esI?"+":"−"} $ {fmtMoney(t.monto)}
                  </span>
                </div>
              );
            })
          }
        </Section>
      </aside>

      <div style={{ flex:1, overflowY:"auto", background:C.fondo, padding:"16px 20px" }}>
        <div style={{ fontFamily:"serif", fontSize:18, color:C.tierra, fontWeight:600, marginBottom:16 }}>Economía</div>
        <Tabs tabs={tabs} active={tab} onChange={setTab}/>
        {tab==="movimientos" && <TabMovimientos transacciones={transacciones} onAdd={onAddTransaccion}/>}
        {tab==="resumen"     && <TabResumen transacciones={transacciones}/>}
      </div>
    </div>
  );
}
